if (!customElements.get('product-hero')) {
  customElements.define(
    'product-hero',
    class ProductHero extends HTMLElement {
      connectedCallback() {
        const jsonEl = this.querySelector('[data-product-hero-json]');
        if (!jsonEl) return;

        this.config = JSON.parse(jsonEl.textContent);
        this.otp = false;
        this.flavorRows = Array.from(this.querySelectorAll('[data-qs-flavor]'));
        this.flavorQty = Object.create(null);
        this.flavorRows.forEach((row) => {
          const key = this.flavorKey(row);
          if (!key) return;
          this.flavorQty[key] = Number(row.dataset.defaultQty) || 0;
        });
        if (this.totalBags() < 1 && this.flavorRows[0]) {
          const firstKey = this.flavorKey(this.flavorRows[0]);
          if (firstKey) this.flavorQty[firstKey] = 1;
        }
        this.variants = (this.config.variants || []).map((variant) => this.parseVariant(variant));
        this.packButtons = Array.from(this.querySelectorAll('[data-qs-pack]'));
        this.maxBags = this.packButtons.reduce((max, pack) => {
          return Math.max(max, Number(pack.dataset.bags) || 0);
        }, 0);
        this.variants.forEach((variant) => {
          this.maxBags = Math.max(this.maxBags, variant.bags || 0);
        });
        if (!this.maxBags) this.maxBags = Number(this.config.defaultBags) || 3;
        this.bags = this.totalBags();

        this.bindGallerySwipe();
        this.bindVideoSwipe();
        this.bindFlavors();
        this.bindOtp();
        this.bindVideos();
        this.bindCertificate();
        this.bindNutrition();
        this.bindStickyBar();
        this.mountLightLabsWidget(this.querySelector('lightlabs-widget'));
        this.nutritionOverlay = this.mountNutritionOverlay(
            this.querySelector('[data-qs-nutrition-overlay], .product-hero__nutrition-overlay') || this.nutritionOverlay
        );
        this.boundLayout = this.layoutDynamicTracks.bind(this);
        window.addEventListener('resize', this.boundLayout);
        this.resizeObserver = new ResizeObserver(this.boundLayout);
        this.resizeObserver.observe(this);
        this.sync();
        this.layoutVideosTrack();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.boundLayout);
        this.resizeObserver?.disconnect();
        this.stickyObserver?.disconnect();
        window.removeEventListener('scroll', this.onStickyScroll);
        window.removeEventListener('resize', this.onStickyScroll);
      }

      bindGallerySwipe() {
        this.bindHorizontalDrag(this.querySelector('.product-hero__gallery .product__media-list'), {
          minWidth: 750,
          snap: true,
        });
      }

      bindVideoSwipe() {
        this.bindHorizontalDrag(this.querySelector('.product-hero__videos-track'));
      }

      bindHorizontalDrag(list, { minWidth = 0, snap = false } = {}) {
        if (!list || list.__qsSwipeBound) return;
        list.__qsSwipeBound = true;

        let pointerId = null;
        let startX = 0;
        let startScroll = 0;
        let dragging = false;
        let moved = false;

        const endDrag = (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;
          dragging = false;
          list.classList.remove('is-dragging');
          try {
            list.releasePointerCapture(pointerId);
          } catch (error) {
            /* already released */
          }
          pointerId = null;

          if (!moved || !snap) return;

          const viewer = list.closest('slider-component');
          if (viewer && typeof viewer.initPages === 'function') viewer.initPages();
          if (viewer && typeof viewer.sliderItemOffset === 'number' && viewer.sliderItemOffset > 0) {
            const page = Math.round(list.scrollLeft / viewer.sliderItemOffset);
            viewer.setSlidePosition?.(page * viewer.sliderItemOffset);
          }
        };

        list.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (minWidth && !window.matchMedia(`(min-width: ${minWidth}px)`).matches) return;
          if (event.target.closest('.np-gallery-arrow, a, button, video, .product-hero__video.is-playing')) return;

          pointerId = event.pointerId;
          startX = event.clientX;
          startScroll = list.scrollLeft;
          dragging = true;
          moved = false;
          list.classList.add('is-dragging');
          list.setPointerCapture(pointerId);
        });

        list.addEventListener('pointermove', (event) => {
          if (!dragging || event.pointerId !== pointerId) return;
          const dx = event.clientX - startX;
          if (Math.abs(dx) > 4) moved = true;
          list.scrollLeft = startScroll - dx;
          if (moved) event.preventDefault();
        });

        list.addEventListener('pointerup', endDrag);
        list.addEventListener('pointercancel', endDrag);

        list.addEventListener(
          'click',
          (event) => {
            if (!moved) return;
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          },
          true
        );
      }

      flavorKey(row) {
        return String(row?.dataset.flavorKey || '')
          .trim()
          .toLowerCase();
      }

      parseVariant(variant) {
        const text = [variant.title, variant.sku, ...(variant.options || [])].filter(Boolean).join(' ');
        const counts = Object.create(null);
        this.flavorRows.forEach((row) => {
          const key = this.flavorKey(row);
          if (!key) return;
          counts[key] = this.countFlavor(text, key);
        });
        const bags = Object.values(counts).reduce((sum, qty) => sum + qty, 0);
        return Object.assign({}, variant, { bags, counts });
      }

      countFlavor(text, key) {
        const name = this.escapeRegExp(key);
        const source = String(text);
        const namedX = source.match(new RegExp(`\\b${name}\\s*x\\s*(\\d+)\\b`, 'i'));
        if (namedX) return Number(namedX[1]);
        const qtyFirst = source.match(new RegExp(`\\b(\\d+)\\s*x\\s*${name}\\b`, 'i'));
        if (qtyFirst) return Number(qtyFirst[1]);
        return new RegExp(`\\b${name}\\b`, 'i').test(source) ? 1 : 0;
      }

      escapeRegExp(value) {
        return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }

      bindFlavors() {
        this.flavorRows.forEach((row) => {
          const key = this.flavorKey(row);
          row.querySelector('[data-qs-qty-minus]')?.addEventListener('click', () => this.changeFlavor(key, -1));
          row.querySelector('[data-qs-qty-plus]')?.addEventListener('click', () => this.changeFlavor(key, 1));
          row.querySelector('[data-qs-qty-add]')?.addEventListener('click', () => this.changeFlavor(key, 1));
        });
      }

      bindOtp() {
        this.querySelector('[data-qs-otp]')?.addEventListener('click', () => {
          this.addOtpToCart();
        });
      }

      addOtpToCart() {
        const otpVariant = this.findOtpVariant();
        const form = this.querySelector('form[data-type="add-to-cart-form"]');
        const submit = form?.querySelector('[type="submit"]');
        const idInput = this.querySelector('[data-qs-variant-id]');
        const planInput = this.querySelector('[data-qs-selling-plan]');
        if (!otpVariant || this.bags < 1 || !form || !submit) return;

        const previousId = idInput ? idInput.value : '';
        const previousPlan = planInput ? planInput.value : '';
        const previousPlanDisabled = planInput ? planInput.disabled : false;

        if (idInput) idInput.value = String(otpVariant.id);
        if (planInput) {
          planInput.value = '';
          planInput.disabled = true;
        }
        submit.disabled = false;
        submit.removeAttribute('aria-disabled');

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submit);
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }

        if (idInput) idInput.value = previousId;
        if (planInput) {
          planInput.disabled = previousPlanDisabled;
          planInput.value = previousPlan;
        }
      }

      bindVideos() {
        this.querySelectorAll('.product-hero__video').forEach((card) => {
          const video = card.querySelector('video');
          const play = card.querySelector('[data-qs-video-play]');
          if (!video || !play || play.dataset.bound) return;
          play.dataset.bound = 'true';
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');

          const setActive = (active) => {
            card.classList.toggle('is-playing', active);
            video.controls = active;
          };

          const start = () => {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
            setActive(true);
          };

          play.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            start();
          });
          video.addEventListener('play', () => setActive(true));
          video.addEventListener('ended', () => setActive(false));
        });
      }

      bindStickyBar() {
        const sticky = this.querySelector('[data-qs-sticky]');
        const atc = this.querySelector('.product-hero__atc');
        const stickyAtc = sticky?.querySelector('[data-qs-sticky-atc]');
        if (!sticky || !atc) return;

        let atcPassed = false;
        const updateSticky = () => {
          const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
          sticky.classList.toggle('has-sticky', atcPassed && !atBottom);
        };

        this.stickyObserver = new IntersectionObserver((entries) => {
          atcPassed = entries[0].boundingClientRect.bottom <= 0;
          updateSticky();
        });
        this.stickyObserver.observe(atc);
        this.onStickyScroll = updateSticky;
        window.addEventListener('scroll', this.onStickyScroll, { passive: true });
        window.addEventListener('resize', this.onStickyScroll);

        stickyAtc?.addEventListener('click', () => {
          if (stickyAtc.disabled || atc.disabled) return;
          atc.click();
        });
      }

      syncStickyBar(variant, otpVariant) {
        const sticky = this.querySelector('[data-qs-sticky]');
        if (!sticky) return;

        const flavorEl = sticky.querySelector('[data-qs-sticky-flavor]');
        const planEl = sticky.querySelector('[data-qs-sticky-plan]');
        const bagsEl = sticky.querySelector('[data-qs-sticky-bags]');
        const priceEl = sticky.querySelector('[data-qs-sticky-price]');
        const imageEl = sticky.querySelector('[data-qs-sticky-image]');
        const stickyAtc = sticky.querySelector('[data-qs-sticky-atc]');
        const selected = this.otp ? otpVariant : variant;
        const ready = Boolean(selected?.available) && this.bags >= 1;

        const flavorParts = [];
        this.flavorRows.forEach((row) => {
          const qty = this.flavorQty[this.flavorKey(row)] || 0;
          if (qty <= 0) return;
          const name = row.querySelector('.product-hero__flavor-name')?.textContent?.trim() || this.flavorKey(row);
          flavorParts.push(qty > 1 ? `${name} ×${qty}` : name);
        });

        if (flavorEl) {
          flavorEl.textContent = flavorParts.join(', ');
          flavorEl.hidden = flavorParts.length === 0;
        }
        if (planEl) {
          planEl.textContent = this.otp ? 'One-time' : 'Monthly';
          planEl.hidden = this.bags < 1;
        }
        if (bagsEl) {
          const bagWord = this.bags === 1 ? 'Bag' : 'Bags';
          bagsEl.textContent = this.bags > 0 ? `${this.bags} ${bagWord}` : '';
          bagsEl.hidden = this.bags < 1;
        }
        if (priceEl && selected) {
          const amount = this.otp ? selected.price : selected.subscriptionPrice || selected.price;
          priceEl.textContent = this.formatMoney(amount);
        }
        if (imageEl) {
          const imageSrc = selected?.image || this.config.productImage || '';
          if (imageSrc) imageEl.src = imageSrc;
        }
        if (stickyAtc) {
          stickyAtc.disabled = !ready;
          stickyAtc.setAttribute('aria-disabled', ready ? 'false' : 'true');
        }
      }

      bindNutrition() {
        const trigger = this.querySelector('.product-hero__nutrition');
        if (!trigger || trigger.__qsNutritionBound) return;
        trigger.__qsNutritionBound = true;
        trigger.addEventListener('click', () => {
          const overlay =
            this.nutritionOverlay ||
            this.querySelector('[data-qs-nutrition-overlay], .product-hero__nutrition-overlay') ||
            document.querySelector('.product-hero__nutrition-overlay');
          if (overlay && typeof window.openNutritionPopup === 'function') {
            window.openNutritionPopup(overlay);
          }
        });
      }

      bindCertificate() {
        if (window.__qsCertBound) return;
        const certLink = this.querySelector('[data-qs-cert-link], .product-hero__cert-link');
        if (!certLink || certLink.__qsCertBound) return;
        if (certLink.tagName === 'A' && certLink.getAttribute('href') && certLink.getAttribute('href') !== '#') {
          return;
        }
        certLink.__qsCertBound = true;
        certLink.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openCertificate();
        });
      }

      mountNutritionOverlay(overlay) {
        if (overlay && overlay.parentElement !== document.body) {
          document.body.appendChild(overlay);
        }
        return overlay;
      }

      mountLightLabsWidget(widget) {
        if (widget && widget.parentElement !== document.body) {
          document.body.appendChild(widget);
        }
        return widget;
      }

      getLightLabsOpener(widget) {
        if (typeof widget?.openModal === 'function') return widget.openModal.bind(widget);
        if (typeof window.lightlabs?.openModal === 'function') return window.lightlabs.openModal;
        return null;
      }

      waitForLightLabsModal(widget, timeout = 5000) {
        return new Promise((resolve) => {
          const started = Date.now();
          const tick = () => {
            const open = this.getLightLabsOpener(widget);
            if (open) {
              open();
              resolve(true);
              return;
            }
            if (Date.now() - started > timeout) {
              resolve(false);
              return;
            }
            requestAnimationFrame(tick);
          };
          tick();
        });
      }

      async openCertificate() {
        let widget = this.mountLightLabsWidget(
          this.querySelector('lightlabs-widget') || document.querySelector('.product-hero__lightlabs, lightlabs-widget')
        );

        if (this.getLightLabsOpener(widget)) {
          this.getLightLabsOpener(widget)();
          return;
        }

        try {
          const response = await fetch(
            `https://app.lightlabs.com/pip_api/products?company_id=416&widget_type=widget&referrer_url=${encodeURIComponent(
              window.location.origin + window.location.pathname
            )}`
          );
          if (!response.ok) throw new Error('lightlabs');
          const data = await response.json();
          const products = data.products || [];
          const href = decodeURIComponent(window.location.href).toLowerCase();
          const matched =
            products.find((product) =>
              (product.slugsToMatch || []).some((slug) => href.includes(String(slug).toLowerCase()))
            ) || products[0];

          if (!matched) throw new Error('no-product');

          const next = document.createElement('lightlabs-widget');
          next.className = 'product-hero__lightlabs';
          next.setAttribute('company-id', '416');
          next.setAttribute('product-id', String(matched.id));
          if (widget) {
            widget.replaceWith(next);
          } else {
            document.body.appendChild(next);
          }
          this.mountLightLabsWidget(next);

          const opened = await this.waitForLightLabsModal(next);
          if (!opened) {
            window.open(
              `https://app.lightlabs.com/comprehensive-testing-report/416/${matched.id}`,
              '_blank',
              'noopener'
            );
          }
        } catch (error) {
          window.open('https://app.lightlabs.com/comprehensive-testing-report/416', '_blank', 'noopener');
        }
      }

      changeFlavor(key, delta) {
        const current = this.flavorQty[key] || 0;
        let next = current + delta;
        if (next < 0) next = 0;
        const others = this.totalBags() - current;
        if (others + next > this.maxBags) next = Math.max(this.maxBags - others, 0);
        this.flavorQty[key] = next;
        this.bags = this.totalBags();
        this.otp = false;
        this.sync();
      }

      totalBags() {
        return Object.values(this.flavorQty).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
      }

      layoutDynamicTracks() {
        this.layoutPackTrack();
        this.layoutVideosTrack();
      }

      layoutVideosTrack() {
        const track = this.querySelector('.product-hero__videos-track');
        if (!track) return;

        const next = `${Math.max(0, document.documentElement.clientWidth - track.getBoundingClientRect().left)}px`;
        if (track.style.width !== next) track.style.width = next;
      }

      packFillPercent() {
        const packs = this.packButtons || [];
        if (packs.length < 2) return 0;

        const stops = packs.map((pack) => Number(pack.dataset.bags) || 0);
        const bags = Number(this.bags) || 0;
        const last = stops.length - 1;

        if (bags <= stops[0]) return 0;
        if (bags >= stops[last]) return 100;

        for (let i = 0; i < last; i++) {
          const from = stops[i];
          const to = stops[i + 1];
          if (bags > to) continue;
          const t = to === from ? 1 : (bags - from) / (to - from);
          return ((i + t) / last) * 100;
        }

        return 100;
      }

      layoutPackTrack() {
        const root = this.querySelector('.product-hero__packs');
        const track = this.querySelector('.product-hero__pack-track');
        const dots = Array.from(this.querySelectorAll('.product-hero__pack-dot'));
        if (!root || !track || dots.length < 2) {
          return;
        }

        const rootBox = root.getBoundingClientRect();
        const first = dots[0].getBoundingClientRect();
        const last = dots[dots.length - 1].getBoundingClientRect();
        const start = first.left + first.width / 2 - rootBox.left;
        const end = last.left + last.width / 2 - rootBox.left;

        track.style.top = `${first.top - rootBox.top + first.height / 2 - track.offsetHeight / 2}px`;
        track.style.left = `${start}px`;
        track.style.width = `${Math.max(end - start, 0)}px`;
      }

      findVariant() {
        if (this.bags < 1 || !this.flavorRows.length) return null;

        return (
          this.variants.find((variant) => {
            return this.flavorRows.every((row) => {
              const key = this.flavorKey(row);
              return (variant.counts?.[key] || 0) === (this.flavorQty[key] || 0);
            });
          }) || null
        );
      }

      activeFlavorKey() {
        let bestKey = this.flavorRows[0] ? this.flavorKey(this.flavorRows[0]) : '';
        let bestQty = -1;
        this.flavorRows.forEach((row) => {
          const key = this.flavorKey(row);
          const qty = this.flavorQty[key] || 0;
          if (qty > bestQty) {
            bestQty = qty;
            bestKey = key;
          }
        });
        return bestKey;
      }

      findOtpVariant() {
        const key = this.activeFlavorKey();
        return (
          this.variants.find((variant) => {
            if ((variant.bags || 0) !== 1) return false;
            return this.flavorRows.every((row) => {
              const flavor = this.flavorKey(row);
              const count = variant.counts?.[flavor] || 0;
              return flavor === key ? count === 1 : count === 0;
            });
          }) ||
          this.variants.find((variant) => (variant.bags || 0) === 1) ||
          null
        );
      }

      toLocalCents(cents) {
        const shopCents = Number(cents) || 0;
        const rate = Number(window.Shopify?.currency?.rate);
        if (Number.isFinite(rate) && rate > 0) {
          return Math.round(shopCents * rate);
        }
        return shopCents;
      }

      formatShopMoney(cents, withTrailingZeros = false) {
        return this.formatMoney(this.toLocalCents(cents), withTrailingZeros);
      }

      currencyCode() {
        return this.config.currency || window.Shopify?.currency?.active || 'USD';
      }

      localeTag() {
        const locale = this.config.locale || window.Shopify?.locale || '';
        const country = this.config.country || window.Shopify?.country || '';
        if (locale && country && !String(locale).includes('-')) return `${locale}-${country}`;
        return locale || undefined;
      }

      formatMoney(cents, withTrailingZeros = false) {
        const amount = (Number(cents) || 0) / 100;
        const currency = this.currencyCode();
        const digits = withTrailingZeros || !Number.isInteger(amount) ? 2 : 0;
        try {
          return new Intl.NumberFormat(this.localeTag(), {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
            minimumFractionDigits: digits,
            maximumFractionDigits: 2,
          }).format(amount);
        } catch (error) {
          const formatted = amount.toFixed(digits);
          return withTrailingZeros ? `${formatted} ${currency}` : `${formatted.replace(/\.00$/, '')} ${currency}`;
        }
      }

      syncPackPrices() {
        const listCents = this.listBagCents();
        (this.packButtons || []).forEach((pack) => {
          const priceEl = pack.querySelector('[data-qs-pack-price]');
          const saveEl = pack.querySelector('[data-qs-pack-save]');
          const priceCents = Number(pack.dataset.priceCents);
          const bags = Number(pack.dataset.bags) || 0;
          const discount = Number(pack.dataset.discount) || 0;
          if (priceEl && priceCents) priceEl.textContent = `${this.formatShopMoney(priceCents)}/ea`;
          if (!saveEl) return;
          const saveCents = discount > 0 && listCents > 0 && bags > 0
            ? Math.round((listCents * discount) / 100) * bags
            : 0;
          if (saveCents > 0) {
            saveEl.hidden = false;
            saveEl.textContent = `Save ${this.formatShopMoney(saveCents)}`;
          } else {
            saveEl.hidden = true;
            saveEl.textContent = '';
          }
        });
        const giftCompare = this.querySelector('[data-qs-gift-compare]');
        const gifts = this.querySelector('[data-qs-gifts]');
        let giftsValue = '';
        if (gifts?.dataset.cents) {
          giftsValue = this.formatShopMoney(Number(gifts.dataset.cents));
        } else {
          const giftItems = this.querySelectorAll('[data-qs-gift-item-cents]');
          if (giftItems.length) {
            let giftsTotal = 0;
            giftItems.forEach((item) => {
              giftsTotal += Number(item.dataset.qsGiftItemCents) || 0;
            });
            giftsValue = this.formatMoney(giftsTotal);
          }
        }
        if (giftCompare && giftsValue) giftCompare.textContent = giftsValue;
        this.giftsValueText = giftsValue;
      }

      listBagCents() {
        const packs = this.packButtons || [];
        const oneBag = packs.find((pack) => Number(pack.dataset.bags) === 1);
        if (oneBag?.dataset.priceCents) return Number(oneBag.dataset.priceCents);
        const zeroDiscount = packs.find((pack) => Number(pack.dataset.discount) === 0 && pack.dataset.priceCents);
        if (zeroDiscount) return Number(zeroDiscount.dataset.priceCents);
        return Number(packs[0]?.dataset.priceCents) || 0;
      }

      nutritionImageKey() {
        const variant = this.findVariant();
        const active = this.flavorRows.filter((row) => (this.flavorQty[this.flavorKey(row)] || 0) > 0);
        if (!variant || this.bags < 1 || active.length !== 1) return 'mix';
        return this.flavorKey(active[0]);
      }

      syncNutritionImage() {
        const overlay =
          this.nutritionOverlay ||
          this.querySelector('[data-qs-nutrition-overlay], .product-hero__nutrition-overlay');
        const image = overlay?.querySelector('.product-hero__nutrition-image, [data-qs-nutrition-image], img');
        if (!overlay || !image) return;

        const key = this.nutritionImageKey();
        const attr = `image${key.charAt(0).toUpperCase()}${key.slice(1)}`;
        const url = overlay.dataset[attr] || overlay.dataset.imageMix || overlay.dataset.imageDefault;
        if (!url || image.getAttribute('src') === url) return;
        image.setAttribute('src', url);
        image.removeAttribute('srcset');
      }

      syncBundleHelper() {
        const helper = this.querySelector('[data-qs-bundle-helper]');
        if (!helper) return;

        const bags = Math.max(Number(this.bags) || 0, 0);
        const scoops = bags;
        const proteinEach = Number(helper.dataset.proteinPerScoop || 20) || 20;
        const protein = scoops * proteinEach;
        const scoopWord = scoops === 1 ? 'scoop' : 'scoops';
        const bagWord = bags === 1 ? 'bag' : 'bags';
        const template =
          helper.dataset.helperTemplate ||
          '[scoops] [scoop_word]/day = [protein]g protein = [bags] [bag_word]/month';

        if (!template.includes('[')) {
          helper.textContent = `${scoops} ${scoopWord}/day = ${protein}g protein = ${bags} ${bagWord}/month`;
          return;
        }

        helper.textContent = template
          .replaceAll('[scoops]', String(scoops))
          .replaceAll('[scoop_word]', scoopWord)
          .replaceAll('[protein]', String(protein))
          .replaceAll('[bags]', String(bags))
          .replaceAll('[bag_word]', bagWord);
      }

      sync() {
        const bundleVariant = this.findVariant();
        const otpVariant = this.findOtpVariant();
        const variant = this.otp ? otpVariant : bundleVariant;
        const packs = this.packButtons || [];
        const exactIndex = packs.findIndex((pack) => Number(pack.dataset.bags) === this.bags);
        const activeIndex = exactIndex >= 0
          ? exactIndex
          : packs.reduce((best, pack, index) => {
              return Number(pack.dataset.bags) <= this.bags ? index : best;
            }, -1);

        packs.forEach((pack, index) => {
          pack.classList.toggle('is-active', activeIndex >= 0 && index <= activeIndex);
        });

        const fill = this.querySelector('[data-qs-pack-fill]');
        if (fill && packs.length > 1) {
          fill.style.width = `${this.packFillPercent()}%`;
        }
        this.layoutDynamicTracks();
        this.syncPackPrices();
        this.syncBundleHelper();
        this.syncNutritionImage();

        this.flavorRows.forEach((row) => {
          const qty = this.flavorQty[this.flavorKey(row)] || 0;
          row.classList.toggle('is-empty', qty === 0);
          const valueEl = row.querySelector('[data-qs-qty]');
          if (valueEl) valueEl.textContent = String(qty).padStart(2, '0');
          const minus = row.querySelector('[data-qs-qty-minus]');
          const plus = row.querySelector('[data-qs-qty-plus]');
          if (minus) minus.disabled = qty <= 0;
          if (plus) plus.disabled = this.bags >= this.maxBags;
        });

        const gifts = this.querySelector('[data-qs-gifts]');
        if (gifts) {
          const unlocked = this.bags >= Number(gifts.dataset.unlockAt || this.config.giftUnlockAt || 1);
          gifts.classList.toggle('is-locked', !unlocked);
          const value = this.giftsValueText || '';
          gifts.querySelectorAll('[data-qs-gifts-title]').forEach((titleEl) => {
            if (!unlocked) {
              titleEl.textContent = gifts.dataset.lockedTitle;
              return;
            }

            const mobileTitle = gifts.dataset.unlockedTitleMobile || gifts.dataset.unlockedTitle;
            const template =
              titleEl.getAttribute('data-qs-gifts-title') === 'mobile' ? mobileTitle : gifts.dataset.unlockedTitle;
            titleEl.textContent = template.replaceAll('[value]', value);
          });
        }

        const otpButton = this.querySelector('[data-qs-otp]');
        if (otpButton) {
          const otpReady = this.bags >= 1 && Boolean(otpVariant?.available);
          otpButton.disabled = !otpReady;
          otpButton.setAttribute('aria-disabled', otpReady ? 'false' : 'true');
          otpButton.classList.toggle('is-active', this.otp);
          const labelEl = otpButton.querySelector('[data-qs-otp-label]');
          if (labelEl) {
            const template = this.config.otpLabel || 'One Time Purchase for [price] (Without free-gifts)';
            const otpPrice = otpVariant?.price;
            if (otpPrice != null) {
              labelEl.textContent = template.replace('[price]', this.formatMoney(otpPrice, true));
            }
          }
        }

        const idInput = this.querySelector('[data-qs-variant-id]');
        const planInput = this.querySelector('[data-qs-selling-plan]');
        const atc = this.querySelector('.product-hero__atc');
        const priceEl = this.querySelector('[data-qs-atc-price]');

        if (idInput) idInput.value = variant ? variant.id : '';
        if (planInput) {
          const usePlan = !this.otp && variant && variant.sellingPlanId;
          planInput.disabled = !usePlan;
          planInput.value = usePlan ? String(variant.sellingPlanId) : '';
        }
        if (atc) {
          const atcReady = Boolean(variant?.available) && this.bags >= 1;
          atc.disabled = !atcReady;
          atc.setAttribute('aria-disabled', atcReady ? 'false' : 'true');
        }
        if (variant && priceEl) {
          const amount = this.otp ? variant.price : variant.subscriptionPrice || variant.price;
          priceEl.textContent = this.formatMoney(amount);
        }
        this.syncStickyBar(variant, otpVariant);
      }
    }
  );
}
